import keyBy from 'lodash.keyby';
import Utils from 'legacy/helpers/utils';
import Vue from 'vue';
import {defaultRule} from 'store/modules/bidmanager/utils/utils';
import {STORE_TYPES} from '../utils/common';
import {getDefaultState, getDefaultFallBackRule, getRunEverydayDays, getRunOnceTime, fallbackRuleUid} from '..';


export default {

  // 1 BidManagerGroupListPage
  [STORE_TYPES.SET_CURRENT_BIDMANAGER_GROUP] (state, group) {
    state.currentBidmanagerGroup = group;
  },
  [STORE_TYPES.SET_PROGRESS_METRICS_LOADING] (state, isLoading) {
    state.isProgressMetricsLoading = isLoading;
  },
  [STORE_TYPES.SET_CURRENT_PAGE] (state, page) {
    state.currentPage = page;
  },
  [STORE_TYPES.SET_PLACEMENTS] (state, placements) {
    state.placements = keyBy(placements, 'id');
  },
  [STORE_TYPES.SET_GROUPS] (state, groups) {
    state.groups = groups;
  },
  [STORE_TYPES.SET_GROUPS_TOTAL] (state, groupsTotal) {
    state.groupsTotal = groupsTotal;
  },
  [STORE_TYPES.SET_PROGRESS_DATA] (state, data) {
    state.progressData = data;
    state.progressDataMap = keyBy(data, 'id');

    state.groups = state.groups.map(group => ({
      ...group,
      progressMetricsStats: state.progressDataMap[group.id] || null
    }));
  },
  [STORE_TYPES.SET_SORTING] (state, sorting) {
    state.sorting = sorting;
  },
  [STORE_TYPES.SET_PER_PAGE] (state, perPage) {
    state.perPage = perPage;
  },

  // 2 Bidmanager
  [STORE_TYPES.FIX_SERVER_GROUP_STATE] (state) {
    state.serverGroupState = Utils.deepCopy(state.group);
  },
  [STORE_TYPES.CHANGE_CHANNEL] (state, channelId) {
    state.group.channel_id = channelId;
  },
  [STORE_TYPES.INIT_GROUP] (state, group) {
    state.group = group;

    if (group.autostrategy_id) {
      state.group.options.rules = [];
    }

    // create uid for rule
    state.group.options.rules = state.group.options.rules.map(i => ({
      ...i,
      conditions: i.conditions.map(c => ({...c, uid: Utils.getUid()})),
      ...{uid: Utils.getUid()}
    }));

    // create fallback rule from 1st rule without condition
    const firstRuleWithoutCondition = state.group.options.rules.filter(
      rule => rule.conditions.length === 0
    )[0];

    const fallbackRule = firstRuleWithoutCondition
      ? {...firstRuleWithoutCondition, uid: fallbackRuleUid} : getDefaultFallBackRule();
    Vue.set(state.group.options, 'fallbackRule', fallbackRule);

    // save rules with filled conditions
    state.group.options.rules = state.group.options.rules.filter(
      rule => rule.conditions.length > 0
    );

    // make keyword_text conditions fields' values unique (old bidmanager had duplicates in keyword text field)
    state.group.options.rules.
      map(rule => rule.conditions).
      flat().
      filter(condition => condition.field === 'keyword_text').
      forEach(condition => {
        condition.values[0] = Array.from(new Set(condition.values[0]));
      });
  },
  [STORE_TYPES.SET_LABELS] (state, labels) {
    state.group.labels = labels;
  },
  [STORE_TYPES.SET_PLACEMENT_IDS] (state, placementIds) {
    state.group.placement_ids = placementIds;
  },
  [STORE_TYPES.ADD_DEFAULT_RULE] (state) {
    state.group.options.rules.push({
      ...defaultRule(state.group.channel_id, state.group.options.biddable, Utils.getUid()),
      uid: Utils.getUid()
    });
  },
  [STORE_TYPES.TOGGLE_RULE] (state, payload) {
    const {enabled, ruleIndex} = payload;

    state.group.options.rules[ruleIndex].enabled = enabled;
  },
  [STORE_TYPES.ADD_RULE] (state) {
    state.group.options.rules.push({
      enabled: true,
      conditions: [],
      action: {},
      uid: Utils.getUid()
    });
  },
  [STORE_TYPES.DELETE_RULE] (state, index) {
    state.group.options.rules.splice(index, 1);

    // we need to reset errors or they will be shown in wrong controls
    state.errors = getDefaultState().errors;
    state.validationRulesErrors = getDefaultState().validationRulesErrors;
  },
  [STORE_TYPES.COPY_RULE] (state, index) {
    const rule = Utils.deepCopy(state.group.options.rules[index]);
    rule.uid = Utils.getUid();
    state.group.options.rules.splice(index + 1, 0, rule);

    // we need to reset errors or they will be shown in wrong controls
    state.errors = getDefaultState().errors;
    state.validationRulesErrors = getDefaultState().validationRulesErrors;
  },
  [STORE_TYPES.SET_RULE] (state, payload) {
    const {rule, index} = payload;
    Vue.set(state.group.options.rules, index, rule);
  },
  [STORE_TYPES.MOVE_RULES] (state, payload) {
    state.group.options.rules = payload;

    // we need to reset errors or they will be shown in wrong controls
    state.errors = getDefaultState().errors;
    state.validationRulesErrors = getDefaultState().validationRulesErrors;
  },
  [STORE_TYPES.FLUSH_STORE] (state) {
    Object.assign(state, getDefaultState());
  },
  [STORE_TYPES.RESET_RULES] (state) {
    state.group.options.rules = [];
  },
  [STORE_TYPES.SET_ACTION] (state, payload) {
    const {action, ruleIndex} = payload;

    state.group.options.rules[ruleIndex].action = action;
  },
  [STORE_TYPES.ADD_CONDITION] (state, payload) {
    const {condition, ruleIndex} = payload;

    state.group.options.rules[ruleIndex].conditions.push({...condition, uid: Utils.getUid()});
  },
  [STORE_TYPES.REMOVE_CONDITION] (state, payload) {
    const {ruleIndex, conditionIndex} = payload;

    state.group.options.rules[ruleIndex].conditions.splice(conditionIndex, 1);
  },
  [STORE_TYPES.SET_CONDITION] (state, payload) {
    const {ruleIndex, conditionIndex, condition} = payload;

    const rule = state.group.options.rules[ruleIndex];
    rule.conditions.splice(conditionIndex, 1, condition);

    // clear condition errors
    try {
      state.errors.options.rules[rule.uid].conditions[condition.uid] = [];
    } catch (e) {

      // expect TypeError in case some of error objects is
      if (e.name !== 'TypeError') {
        throw e;
      }
    }
  },
  [STORE_TYPES.SET_FALLBACK_ACTION] (state, payload) {
    Vue.set(state.group.options.fallbackRule, 'action', payload.action);
  },
  [STORE_TYPES.TOGGLE_FALLBACK_ACTION_STATE] (state, payload) {
    Vue.set(state.group.options.fallbackRule, 'enabled', payload.state);
  },
  [STORE_TYPES.SET_VALUE] (state, payload) {
    const {ruleIndex, conditionIndex, value, valueIndex} = payload;

    state.group.options.rules[ruleIndex].conditions[conditionIndex].values.splice(valueIndex, 1, value);
  },
  [STORE_TYPES.SET_BIDDABLE_TYPES] (state, choices) {
    state.biddableTypesChoices = choices;
  },
  [STORE_TYPES.IS_LOGGING_AVAILABLE] (state, payload) {
    state.isLoggingAvailable = payload;
  },
  [STORE_TYPES.SET_ENABLE_ON] (state, value) {
    if (!value) {
      if (state.group.placement_ids.length) {
        value = 'placements';
      } else {

        // if labels or default value
        value = 'labels';
      }
    }

    state.enableOn = value;
  },
  [STORE_TYPES.UPDATE_GROUP] (state, obj) {
    Object.keys(obj).forEach((k) => {
      Vue.set(state.group, k, obj[k]);
    });
  },
  [STORE_TYPES.UPDATE_GROUP_OPTIONS] (state, obj) {
    function defaultUpdateCallbackForKey (key) {
      Vue.set(state.group.options, key, obj[key]);
    }

    function customUpdateCallbackForBiddable () {
      // placement urls are started once a day, this is to reset time schedule
      if (obj.biddable === 'PLACEMENT_URL') {
        Vue.set(state.group, 'day_of_week', getRunEverydayDays());
        Vue.set(state.group, 'time_of_day', getRunOnceTime());
      }
      defaultUpdateCallbackForKey('biddable');
    }

    function customUpdateCallbackForRules () {
      // create fallback rule from 1st rule without condition
      const firstRuleWithoutCondition = obj.rules.find(rule => rule.conditions.length === 0);
      if (firstRuleWithoutCondition) {
        Vue.set(state.group.options, 'fallbackRule', firstRuleWithoutCondition);
        Vue.set(state.group.options, 'rules', obj.rules.filter(rule => rule.conditions.length > 0));
      } else {
        defaultUpdateCallbackForKey('rules');
      }
    }

    const customUpdateCallback = {
      biddable: customUpdateCallbackForBiddable,
      rules: customUpdateCallbackForRules

    };

    Object.keys(obj).forEach((key) => {
      const customUpdateCallbackForKey = customUpdateCallback[key];
      if (customUpdateCallbackForKey) {
        customUpdateCallbackForKey();
      } else {
        defaultUpdateCallbackForKey(key);
      }
    });
  },

  // PRIVATE
  [STORE_TYPES.IS_NEW] (state, isNew) {
    state.isNew = isNew;
  },
  [STORE_TYPES.IS_SELECTABLE_BIDDABLE_FOR_PLACEMENT] (state, isSelectableBiddableForPlacement) {
    state.isSelectableBiddableForPlacement = isSelectableBiddableForPlacement;
  },
  [STORE_TYPES.SET_FETCHED_FIELDS] (state, fields) {
    state.fields = fields;
  },
  [STORE_TYPES.SET_FETCHED_DATEFRAMES] (state, dateframes) {
    state.dateframes = dateframes;
  },
  [STORE_TYPES.SET_FETCHED_ACTIONS] (state, actions) {
    state.actions = actions;
  },
  [STORE_TYPES.SET_FETCHED_TYPES] (state, types) {
    state.types = types;
  },
  [STORE_TYPES.SET_FETCHED_OPERATORS] (state, operators) {
    state.operators = operators;
  },
  [STORE_TYPES.SET_FETCHED_PLACEMENTS] (state, placements) {
    state.placementChoices = placements;
  },
  [STORE_TYPES.SET_FETCHED_PROFILES] (state, profiles) {
    state.profiles = profiles;
  },
  [STORE_TYPES.SET_FETCHED_GOALS] (state, goals) {
    state.goals = goals;
  },
  [STORE_TYPES.SET_FETCHED_LABELS] (state, labels) {
    state.labelChoices = labels;
  },
  [STORE_TYPES.SET_FETCHED_POSITIONS] (state, positions) {
    state.positions = positions;
  },
  [STORE_TYPES.SET_FETCHED_PARAMS] (state, params) {
    const {positions, fields, dateframes, actions, types, operators, biddable} = params;

    positions ? state.positions = positions : null;
    state.fields = fields;
    state.dateframes = dateframes;
    state.actions = actions;
    state.types = types;
    state.operators = operators;

    /*
     * biddable only specified for channels with single biddable
     * (for now used only for default name generation)
     */
    state.biddableForDefaultName = biddable;
  },
  [STORE_TYPES.SET_FETCHED_PROGRESS_METRICS] (state, progressMetrics) {
    state.progressMetrics = progressMetrics;
  },
  [STORE_TYPES.SET_VALIDATION_RULES_ERRORS] (state, errors = {}) {
    Vue.set(state, 'validationRulesErrors', errors);
  },
  [STORE_TYPES.SET_ERRORS] (state, errors = {}) {
    const ruleErrors = (errors.options || {}).rules || {};

    if (Object.keys(ruleErrors).length > 0) {
      const newRuleErrors = {};
      Object.keys(ruleErrors).forEach(index => {

        // indexing in errors from backend starts from 1 (not from 0)
        let rule = state.group.options.rules[index - 1];

        // if index is for last rule and fallbackRule is enabled, it is error for fallbackRule
        if (state.group.options.rules.length === index - 1 &&
          state.group.options.fallbackRule.enabled) {
          rule = state.group.options.fallbackRule;
        }
        const newConditionErrors = {};

        Object.keys(ruleErrors[index].conditions || {}).forEach(conditionIndex => {
          newConditionErrors[rule.conditions[conditionIndex - 1].uid] =
            ruleErrors[index].conditions[conditionIndex];
        });

        newRuleErrors[rule.uid] = ruleErrors[index];
        newRuleErrors[rule.uid].conditions = newConditionErrors;
      });
      errors.options.rules = newRuleErrors;
    }

    Vue.set(state, 'errors', errors);
  },
  [STORE_TYPES.SET_LOADING_CHANNEL_STATE] (state, isLoading) {
    state.channelLoading = isLoading;
  },
  [STORE_TYPES.SET_STRATEGY_TYPE] (state, type) {
    state.strategyType = type;
  },
  [STORE_TYPES.SET_AUTOSTRATEGY_ID] (state, id) {
    state.group.autostrategy_id = id;
  },
  [STORE_TYPES.RESET_AUTOSTRATEGY_ARGUMENTS] (state, fields) {
    const newArguments = {};
    for (const field of fields) {
      newArguments[field] = undefined;
    }
    state.group.autostrategy_arguments_values = newArguments;
  },
  [STORE_TYPES.RESET_AUTOSTRATEGY] (state) {
    state.group.autostrategy_id = null;
    state.group.autostrategy_arguments_values = {};
  },
  [STORE_TYPES.SET_AUTOSTRATEGY_ARGUMENT] (state, [attribute, value]) {
    Vue.set(state.group.autostrategy_arguments_values, attribute, value);
  },
  [STORE_TYPES.SET_STRATEGIES_LIST] (state, list) {
    state.autostrategiesList = list;
  }
};
